import React, { forwardRef, ReactNode, useEffect, useState } from 'react'
import {
  Box,
  createPolymorphicComponent,
  createStyles,
  MantineSize,
  Select,
  SelectItem,
  SelectProps,
} from '@mantine/core'
import { XelaColor } from '@/XelaReact'
import { omit } from 'lodash'
import { IconX } from '@tabler/icons-react'

interface XSelectProps extends SelectProps {
  placeholder?: string
  label?: ReactNode
  size?: MantineSize
}

const useStyles = createStyles(
  (
    _,
    params: {
      value?: string | null
      placeholder?: string
      focused?: boolean
      label?: ReactNode
      size?: MantineSize
      icon?: ReactNode
    }
  ) => ({
    root: {
      fontFamily: 'Nunito Sans',
      position: 'relative',
      width: '100%',
    },

    input: {
      fontSize: params.size === 'sm' ? '12px' : '14px',
      fontWeight: params.size === 'sm' ? 700 : 700,
      height: params.size === 'sm' ? '45px' : '56px',
      padding: params.label
        ? params.size === 'sm'
          ? '18px 22px 2px'
          : '26px 22px 6px'
        : params.size === 'sm'
        ? '2px 22px 2px'
        : '5px 22px 6px',
      border: '1px solid #E8E8EA',
      borderRadius: params.size === 'sm' ? '16px' : '18px',
      '&:hover, &:focus': {
        padding: params.label
          ? params.size === 'sm'
            ? '17px 21px 1px'
            : '25px 21px 5px'
          : params.size === 'sm'
          ? '1px 21px 1px'
          : '4px 21px 5px',
        border: '2px solid #003CFF',
      },
      '&:focus': {
        color: '#003CFF !important',
      },
      '&::placeholder': {
        color: XelaColor.Gray10,
        fontWeight: 400,
      },
    },

    label: {
      fontSize: params.focused || params.placeholder || params.value ? '10px' : '13px',
      lineHeight: '15px',
      fontWeight: 700,
      position: 'absolute',
      pointerEvents: 'none',
      paddingLeft: '23px',
      transition: 'all 300ms ease',
      paddingTop: params.label
        ? `${
            params.focused || params.placeholder || params.value
              ? params.size === 'sm'
                ? '8px'
                : '12px'
              : params.size === 'sm'
              ? '15px'
              : '20px'
          }`
        : '0px',
      zIndex: 1,
      color: XelaColor.Gray3,
      '&:hover, &:focus': {
        paddingLeft: '25px',
      },
    },

    dropdown: {
      minWidth: '200px !important',
      fontFamily: 'Nunito Sans',
      boxShadow: '0px 8px 12px rgba(0, 0, 0, 0.08), 0px 4px 48px rgba(0, 0, 0, 0.08)',
      borderRadius: '12px',
      padding: '8px !important',
      zIndex: 9999,
    },

    item: {
      width: '90%',
      fontSize: params.size === 'sm' ? '12px' : '14px',
      fontWeight: params.size === 'sm' ? 700 : 700,
      color: '#1D1929',
      marginBottom: '4px !important',
      padding: params.size === 'sm' ? '8px 12px !important' : '12px !important',
      '&[data-selected]': {
        '&, &:hover': {
          color: '#FFFFFF',
          background: '#003CFF !important',
          borderRadius: '12px',
        },
      },
      '&[data-focused]': {
        '&, &:hover': {
          background: '#F9F9F9',
          borderRadius: '12px',
        },
      },
      '&:hover': {
        borderRadius: '12px',
      },
    },
  })
)

// eslint-disable-next-line react/display-name
const _XSelect = forwardRef<
  HTMLInputElement,
  Omit<XSelectProps, 'data'> & {
    data?: SelectItem[] | string[]
    onInit?: (callback: (data: SelectItem[] | string[]) => void) => void
  }
>(({ children, ...props }, ref) => {
  const [data, setData] = useState<SelectItem[] | string[]>([])
  const [val, setVal] = useState(props.value)
  const [focused, setFocused] = useState(false)

  const { classes } = useStyles({
    label: props.label,
    placeholder: props.placeholder,
    icon: props.icon,
    value: val,
    size: props.size,
    focused,
  })

  useEffect(() => {
    setVal(props.value)
  }, [props.value])

  useEffect(() => {
    if (props.data) {
      setData(props.data)
    }
  }, [props])

  useEffect(() => {
    if (props.onInit) {
      props.onInit((data) => {
        if (data && data.length > 0) {
          // Check if data is an array of objects or an array of strings
          const isObjectArray = typeof data[0] === 'object' && data[0] !== null

          if (isObjectArray) {
            setData(data as SelectItem[])

            if (val !== '') {
              // Check if val exists in data, otherwise set the first element's value as val
              if (!val || !(data as SelectItem[]).find((item) => item.value === val)) {
                setVal((data as SelectItem[])[0].value)
                props.onChange && props.onChange((data as SelectItem[])[0].value)
              }
            }
          } else {
            // If data is an array of strings, convert it to the required format
            const convertedData: SelectItem[] = (data as string[]).map((item) => ({
              label: item,
              value: item,
            }))

            setData(convertedData)

            // Check if val exists in data, otherwise set the first element as val
            if (val !== '') {
              if (!val || !(data as string[]).includes(val)) {
                setVal((data as string[])[0])
                props.onChange && props.onChange((data as string[])[0])
              }
            }
          }
        }
      })
    }
  }, [])

  return (
    <Box style={{ position: 'relative', width: '100%' }}>
      <Select
        autoComplete={'off'}
        ref={ref}
        {...omit(props, ['data', 'onInit', 'clearable'])}
        data={data}
        classNames={classes}
        value={val}
        onFocus={(event) => {
          setFocused(true)
          if (props.onFocus) {
            props.onFocus(event)
          }

          if (props.searchable) {
            const input = event.target
            input.setSelectionRange(0, input.value.length)
          }
        }}
        onBlur={(event) => {
          setFocused(false)
          if (props.onBlur) {
            props.onBlur(event)
          }
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          const target = e.target as HTMLInputElement

          if (target.value && target.value !== '') {
            if (e.key === 'Enter' || e.key === 'Tab') {
              let value: string | null = null
              props.data?.map((item) => {
                if (value === null) {
                  if (typeof item === 'string') {
                    if (item.toLowerCase().includes(target.value.toLowerCase())) {
                      value = item
                    }
                  } else {
                    if (item.label && item.label.toLowerCase().includes(target.value.toLowerCase())) {
                      value = item.value
                    }
                  }
                }
              })

              if (value && value !== '') {
                setVal(value)
                if (props.onChange) {
                  props.onChange(value)
                }
              }
            }

            if (props.onKeyDown) {
              props.onKeyDown(e)
            }
          } else {
            setVal('')
            if (props.onChange) {
              props.onChange('')
            }
          }
        }}
        onChange={(value) => {
          if (props.onChange) {
            props.onChange(value)
          }

          setVal(value)
        }}
      >
        {children}
      </Select>
      {props.clearable && (
        <Box
          style={{ position: 'absolute', right: '30px', top: props.size === 'sm' ? '14px' : '22px', cursor: 'pointer' }}
          onClick={() => {
            setVal('')
            if (props.onChange) {
              props.onChange('')
            }
          }}
        >
          <IconX size={13} color={XelaColor.Gray7}></IconX>
        </Box>
      )}
    </Box>
  )
})
export const XSelect = createPolymorphicComponent<
  'input',
  Omit<XSelectProps, 'data'> & {
    data?: SelectItem[] | string[]
    onInit?: (callback: (data: SelectItem[] | string[]) => void) => void
  }
>(_XSelect)

export default XSelect
